/** รูปแบบตัวเลข/เงิน/เวลา ของคอนโซล — ที่เดียวเพื่อให้ทุกจอพูดเหมือนกัน */

const LOCALE = "lo-LA";

/** จำนวนเต็มพร้อมตัวคั่นหลักพัน */
export function num(value: number): string {
  return value.toLocaleString(LOCALE);
}

/**
 * เงินแบบย่อสำหรับ KPI (mockup ใช้ `182.4 ລ້ານ ກີບ`)
 *
 * ย่อเฉพาะตอนถึงหลักล้าน/พันจริง ๆ — ตัวเลขเล็กต้องแสดงเต็มเพราะย่อแล้วเสียความหมาย
 */
export function compactLAK(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} ລ້ານ ກີບ`;
  }
  if (Math.abs(value) >= 10_000) {
    return `${(value / 1_000).toLocaleString(LOCALE, { maximumFractionDigits: 0 })} ພັນ ກີບ`;
  }
  return `${num(value)} ກີບ`;
}

/** เงินเต็มจำนวน ใช้ในตารางที่ต้องบวกเลขได้จริง */
export function fullLAK(value: number): string {
  return `${num(value)} ກີບ`;
}

/**
 * ระยะเวลาที่ใช้ทำเรื่อง — `0` แปลว่า "ยังไม่มีข้อมูล" ตามสัญญาของ API
 * จึงคืนเส้นประ ไม่ใช่ "0 ນາທີ" ซึ่งอ่านเหมือนทำเสร็จทันที
 */
export function duration(seconds: number): string {
  if (seconds <= 0) return "—";
  if (seconds < 60 * 60) return `${String(Math.round(seconds / 60))} ນາທີ`;
  if (seconds < 24 * 60 * 60) {
    return `${(seconds / 3600).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} ຊົ່ວໂມງ`;
  }
  return `${(seconds / 86400).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} ມື້`;
}

/**
 * เปอร์เซ็นต์การเปลี่ยนแปลง — `null`/`undefined` คือไม่มีฐานให้เทียบ
 * ต้องคืน `null` ต่อไป ไม่ใช่ 0% (ดู StatsSummary ใน openapi.yaml)
 */
export function deltaText(pct: number | null | undefined): { text: string; direction: "up" | "down" } | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const rounded = Math.round(pct);
  return {
    text: `${rounded >= 0 ? "▲" : "▼"} ${String(Math.abs(rounded))}%`,
    direction: rounded >= 0 ? "up" : "down",
  };
}

const DATE_FMT = new Intl.DateTimeFormat(LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

/** ป้ายแกนของกราฟรายสัปดาห์/รายเดือน */
export function periodLabel(iso: string, unit: "week" | "month"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  if (unit === "month") return `ເດືອນ ${String(d.getUTCMonth() + 1)}`;
  return `${String(d.getUTCDate())}/${String(d.getUTCMonth() + 1)}`;
}

/** ตัวอักษรแรกของชื่อสำหรับ avatar (ภาษาลาวนับเป็น code point ไม่ใช่ byte) */
export function initial(name: string): string {
  return [...name.trim()].find((c) => c.trim().length > 0) ?? "?";
}

/**
 * รหัสย่อสำหรับแสดงบนจอ — เอา **ท้าย** 8 ตัว ไม่ใช่หน้า 8 ตัว
 *
 * id ในระบบนี้เป็น UUIDv7 ซึ่งเรียงตามเวลา 48 บิตแรกคือ timestamp แถวที่ถูกสร้าง
 * ในนาทีเดียวกันจึงขึ้นต้นเหมือนกันหมด (`#01a0e239` ทุกแถว) — ส่วนที่แยกแถวออกจาก
 * กันได้จริงอยู่ท้าย id
 */
export function shortId(id: string): string {
  // 8 ตัวแรก ตัวพิมพ์ใหญ่ — ตรงกับที่แอปประชาชน (web-mobile/Flutter) และ API (audit summary,
  // request_short_id) ใช้ ผู้ขอบอกเลขจากแอปแล้วเจ้าหน้าที่ค้นในคิวเจอ
  return id.replaceAll("-", "").slice(0, 8).toUpperCase();
}

/** จัดกลุ่มเบอร์ในช่องกรอก — `20 2855 5555` (prefix +856 อยู่นอกช่อง) */
export function formatPhoneNational(input: string): string {
  let d = input.replace(/\D+/g, "");
  if (d.startsWith("856")) d = d.slice(3);
  if (d.startsWith("0")) d = d.slice(1);
  if (d.length > 10) d = d.slice(0, 10);
  if (d.length <= 2) return d;
  const op = d.slice(0, 2);
  const body = d.slice(2);
  const chunks: string[] = [];
  for (let i = 0; i < body.length; i += 4) chunks.push(body.slice(i, i + 4));
  return `${op} ${chunks.join(" ")}`;
}

/** วันเวลาแบบ DD/MM/YYYY HH:mm ตามเวลาลาว — `toLocaleString("lo-LA")` บางเบราว์เซอร์ให้ เดือน/วัน */
export function dateTimeLao(epochMs: number): string {
  const d = new Date(epochMs + 7 * 3_600_000).toISOString();
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)} ${d.slice(11, 16)}`;
}
